import { useTranslation } from "react-i18next";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash2, Users, Award } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface BadgeData {
  id: string;
  name_it: string;
  image_url: string | null;
  badge_type: string;
  is_active: boolean;
  user_count?: number;
}

interface BadgeCardProps {
  badge: BadgeData;
  badgeName: string;
  onEdit: () => void;
  onDelete: () => void;
}

export const BadgeCard = ({ badge, badgeName, onEdit, onDelete }: BadgeCardProps) => {
  const { t } = useTranslation();

  const getBadgeTypeStyle = (type: string) => {
    switch (type) {
      case 'pro':
        return 'bg-blue-500 text-white';
      case 'plus':
        return 'bg-purple-500 text-white';
      default:
        return 'bg-amber-500 text-white';
    }
  };

  const getBadgeTypeLabel = (type: string) => {
    switch (type) {
      case 'pro':
        return 'PRO';
      case 'plus':
        return 'PLUS';
      default:
        return t("admin.badges.typeCustom");
    }
  };

  return (
    <BrutalCard className="overflow-hidden">
      <div className="p-6">
        {/* Header with image and type */}
        <div className="flex items-start gap-4 mb-4">
          <div className="w-16 h-16 flex-shrink-0 border-[3px] border-dark bg-surface flex items-center justify-center overflow-hidden">
            {badge.image_url ? (
              <img
                src={badge.image_url}
                alt={badgeName}
                className="w-full h-full object-contain"
              />
            ) : (
              <Award className="w-8 h-8 text-muted-foreground" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-bold text-lg truncate">{badgeName}</h3>
            <Badge className={`${getBadgeTypeStyle(badge.badge_type)} mt-1`}>
              {getBadgeTypeLabel(badge.badge_type)}
            </Badge>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            <span>{badge.user_count || 0} {t("admin.badges.users")}</span>
          </div>
          <div className={`px-2 py-0.5 rounded text-xs font-bold ${badge.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
            {badge.is_active ? t("admin.badges.active") : t("admin.badges.inactive")}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onEdit}
                  className="flex-1 p-2 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all flex items-center justify-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  <span className="font-bold text-sm">{t("admin.badges.edit")}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("admin.badges.editBadge")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onDelete}
                  className="p-2 border-[3px] border-dark bg-destructive text-destructive-foreground shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("admin.badges.deleteBadge")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </BrutalCard>
  );
};